import { siteConfig } from "../data/siteConfig";

export type SubscribeResult = "subscribed" | "not-configured" | "failed";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TIMEOUT_MS = 12000;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/**
 * Sends the email to the configured form backend (e.g. a Formspree form
 * whose notification recipient is info@liftoffinvestments.co.bw).
 * Returns "subscribed" only when the backend confirms the address was stored.
 * No secrets live here — the endpoint URL is public by design.
 */
export async function subscribe(email: string, honeypot: string): Promise<SubscribeResult> {
  if (!siteConfig.notifyEndpoint) return "not-configured";

  // Bots fill hidden fields; quietly drop the request without storing anything.
  if (honeypot.trim()) return "failed";

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(siteConfig.notifyEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        email: email.trim(),
        _subject: "New launch notification sign-up — Liftoff Investments",
        source: "Coming soon page",
      }),
      signal: controller.signal,
    });
    if (!res.ok) return "failed";

    // Require an explicit confirmation when the backend returns JSON.
    const body: unknown = await res.json().catch(() => null);
    if (body && typeof body === "object" && "ok" in body && (body as { ok: unknown }).ok === false) {
      return "failed";
    }
    return "subscribed";
  } catch {
    return "failed";
  } finally {
    window.clearTimeout(timer);
  }
}
